"use client";

import { useEffect, useLayoutEffect, useRef } from "react";
import { getRoutePattern, type RouteMatcher } from "./routePattern";
import {
  startBrowserTracingNavigationSpan as defaultStartNavigationSpan,
  startBrowserTracingPageLoadSpan as defaultStartPageLoadSpan,
} from "./init";

export { getRoutePattern };

// Captured at module load time. If this module is lazy-loaded via code splitting,
// this will reflect the chunk load time rather than the actual page load time.
const pageloadStartTime = performance.timeOrigin;

type UseRouteSpansArgs = {
  useLocation: () => { pathname: string };
  matchRoutes: RouteMatcher;
  di?: {
    startBrowserTracingPageLoadSpan?: typeof defaultStartPageLoadSpan;
    startBrowserTracingNavigationSpan?: typeof defaultStartNavigationSpan;
  };
};

export function useRouteSpans({ useLocation, matchRoutes, di }: UseRouteSpansArgs) {
  const location = useLocation();
  const isFirstRender = useRef(true);
  const prevPathname = useRef(location.pathname);
  const navStartTime = useRef(0);

  // Capture nav start time synchronously (useLayoutEffect fires before useEffect) so
  // navStartTime.current is set before the effect below emits the navigation span.
  useLayoutEffect(() => {
    if (location.pathname !== prevPathname.current) {
      navStartTime.current = performance.now() + performance.timeOrigin;
    }
  }, [location.pathname]);

  useEffect(() => {
    const routePattern = getRoutePattern(location.pathname, matchRoutes);
    // Resolved at call time so the post-init impl is picked up if init() finished between render and effect.
    const startPageLoadSpan = di?.startBrowserTracingPageLoadSpan ?? defaultStartPageLoadSpan;
    const startNavigationSpan = di?.startBrowserTracingNavigationSpan ?? defaultStartNavigationSpan;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      startPageLoadSpan({
        name: routePattern,
        startTime: pageloadStartTime,
        attributes: { "sentry.source": "route" },
      });
    } else if (location.pathname !== prevPathname.current) {
      startNavigationSpan({
        name: routePattern,
        startTime: navStartTime.current,
        attributes: { "sentry.source": "route" },
      });
    }

    prevPathname.current = location.pathname;
  }, [location.pathname, matchRoutes, di]);
}
